import {
  EditOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PartitionOutlined,
  SettingOutlined,
  ShopOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined
} from "@ant-design/icons";
import { Button, Layout, Menu, Typography } from "antd";
import { FunctionComponent, useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useStateIfMounted } from "use-state-if-mounted";
import {
  ROUTE_DASHBOARD_GEARS,
  ROUTE_DASHBOARD_GEAR_CREATE,
  ROUTE_DASHBOARD_MY_GEARS,
  ROUTE_DASHBOARD_ORGANIZATION, ROUTE_DASHBOARD_PROFILE,
  ROUTE_DASHBOARD_ROLES,
  ROUTE_DASHBOARD_ROLE_CREATE,
  ROUTE_DASHBOARD_SUPER_ADMIN,
  ROUTE_DASHBOARD_SUPER_ADMIN_ORGANIZATIONS,
  ROUTE_DASHBOARD_SUPER_ADMIN_USERS,
  ROUTE_DASHBOARD_TAGS,
  ROUTE_DASHBOARD_TAG_CREATE,
  ROUTE_DASHBOARD_USERS,
  ROUTE_DASHBOARD_USER_CREATE
} from "../../shared/app-constants";
import AppImage from "../../shared/app-image";
import canUser from "../../shared/can-user";
import getDashboardRoute from "../../shared/get-dashboard-route";
import { LocalStorageManager } from "../../shared/local-storage-manager";
import Permissions from "../../shared/permissions";
import logo from "../assets/logo-128.png";
import { AppContext } from "../contexts/app-context";
import HttpClient from "../http-client";
const { SubMenu } = Menu;

const { Header, Sider, Content } = Layout;

interface DashboardLayoutProps { }

type MenuModule = {
  icon: React.FC<{ className?: string }>;
  path: string;
  title: string;
  links: {
    url: string;
    title: string;
  }[];
};

const DashboardLayout: FunctionComponent<DashboardLayoutProps> = ({
  children,
}) => {
  const [collapsed, setCollapsed] = useStateIfMounted(false);
  const { setUser, organization, user } = useContext(AppContext);
  const location = useLocation();
  const signOut = async () => {
    try {
      await HttpClient.post("user/sign-out");
    } catch (e) { }
    LocalStorageManager.clearTokens();
    setUser(undefined);
  };
  const toggle = () => {
    setCollapsed((collapsed) => !collapsed);
  };

  const menuModules: MenuModule[] = useMemo(() => {
    if (!user) return [];
    const gearModule: MenuModule = {
      icon: SettingOutlined,
      path: ROUTE_DASHBOARD_GEARS,
      title: "Gears",
      links: [],
    };
    if (canUser(user, Permissions.VIEW_GEAR)) {
      gearModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_GEARS),
        title: "All Gears",
      });
      if (!user.is_super_admin) {
        gearModule.links.push({
          url: getDashboardRoute({}, ROUTE_DASHBOARD_MY_GEARS),
          title: "My Gears",
        });
      }
    }
    if (canUser(user, Permissions.ADD_GEAR)) {
      gearModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_GEAR_CREATE),
        title: "Create Gear",
      });
    }
    const tagsModule: MenuModule = {
      icon: TagsOutlined,
      path: ROUTE_DASHBOARD_TAGS,
      title: "Tags",
      links: [],
    };
    if (canUser(user, Permissions.VIEW_GEAR)) {
      tagsModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_TAGS),
        title: "All Tags",
      });
    }
    if (canUser(user, Permissions.ADD_GEAR)) {
      tagsModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_TAG_CREATE),
        title: "Create Tag",
      });
    }
    const usersModule: MenuModule = {
      icon: TeamOutlined,
      path: ROUTE_DASHBOARD_USERS,
      title: "Users",
      links: [],
    };
    if (!user.is_super_admin && canUser(user, Permissions.VIEW_USER)) {
      usersModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_USERS),
        title: "All Users",
      });
    }
    if (!user.is_super_admin && canUser(user, Permissions.ADD_USER)) {
      usersModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_USER_CREATE),
        title: "Create User",
      });
    }
    const rolesModule: MenuModule = {
      icon: PartitionOutlined,
      path: ROUTE_DASHBOARD_ROLES,
      title: "Roles",
      links: [],
    };
    if (!user.is_super_admin && canUser(user, Permissions.VIEW_ROLE)) {
      rolesModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_ROLES),
        title: "All Roles",
      });
    }
    if (!user.is_super_admin && canUser(user, Permissions.ADD_ROLE)) {
      rolesModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_ROLE_CREATE),
        title: "Create Role",
      });
    }

    const organizationModule: MenuModule = {
      icon: ShopOutlined,
      path: ROUTE_DASHBOARD_ORGANIZATION,
      title: "Organization",
      links: [],
    };
    if (
      !user.is_super_admin &&
      canUser(user, Permissions.ORGANIZATION_MANAGEMENT)
    ) {
      organizationModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_ORGANIZATION),
        title: "My Organization",
      });
    }

    const superAdminModule: MenuModule = {
      icon: UserOutlined,
      path: ROUTE_DASHBOARD_SUPER_ADMIN,
      title: "Super Admin",
      links: [],
    }

    if (user.is_super_admin) {
      superAdminModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_SUPER_ADMIN_ORGANIZATIONS),
        title: "Organizations",
      });
    }

    if (user.is_super_admin) {
      superAdminModule.links.push({
        url: getDashboardRoute({}, ROUTE_DASHBOARD_SUPER_ADMIN_USERS),
        title: "Users",
      });
    }
    const modules = [
      gearModule,
      tagsModule,
      usersModule,
      rolesModule,
      organizationModule,
    ];
    if (user.is_super_admin) {
      modules.push(superAdminModule);
    }
    return modules;
  }, [user]);
  return (
    <Layout className="dashboard-layout">
      <Sider
        trigger={null}
        className="bg-white sidebar"
        collapsed={collapsed}
        breakpoint="md"
        onBreakpoint={(broken) => {
          setTimeout(() => {
            setCollapsed(broken);
          }, 100);
        }}
      >
        <div className="logo-container">
          <Link to={{ pathname: getDashboardRoute({}) }}>
            <AppImage preview={false} height={55} src={logo} className="logo" />
          </Link>
        </div>
        <div className="menu-container">
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[location.pathname]}
            defaultOpenKeys={menuModules
              .filter((e) => location.pathname.includes(e.path))
              .map((e) => e.path)}
          >
            {menuModules
              .filter((e) => e.links.length > 0)
              .map((module) => (
                <SubMenu
                  key={module.path}
                  icon={<module.icon />}
                  title={module.title}
                >
                  {module.links.map((link) => (
                    <Menu.Item key={link.url}>
                      <Link to={link.url}>{link.title}</Link>
                    </Menu.Item>
                  ))}
                </SubMenu>
              ))}

            <Menu.Item
              key={getDashboardRoute({}, ROUTE_DASHBOARD_PROFILE)}
              icon={<EditOutlined />}
            >
              <Link
                to={{
                  pathname: getDashboardRoute({}, ROUTE_DASHBOARD_PROFILE),
                }}
              >
                Profile
              </Link>
            </Menu.Item>
            <Menu.Item key="1" icon={<LogoutOutlined />} onClick={signOut}>
              Sign Out
            </Menu.Item>
          </Menu>
        </div>
      </Sider>
      <Layout className="site-layout">
        <Header className="bg-white header">
          <Button
            onClick={toggle}
            type="link"
            size="small"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          />
          <Typography.Title className="my-0" level={5}>
            {organization?.name}
          </Typography.Title>
        </Header>
        <Content className="bg-white">{children}</Content>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
